import { Alert, Button, Modal, Skeleton, Typography } from 'antd';
import React, { useEffect, useRef } from 'react';
import PageSection from '@/components/PageSection';
import DetailActions from './DetailActions';
import styles from './index.module.less';

export interface CrmDetailSection {
  key: string;
  label: string;
  count?: number;
  hasOwnShell?: boolean;
  children: React.ReactNode;
}

/** CRM 详情统一留在台账上查看；所有分区在同一页面连续展示。 */
export default function CrmDetailModal({
  open,
  loading,
  error,
  errorStatus,
  title,
  tags,
  summary,
  facts,
  actions,
  sections,
  recordKey,
  onClose,
  onRetry,
  children,
}: {
  open: boolean;
  loading: boolean;
  error?: string;
  errorStatus?: number;
  title: React.ReactNode;
  tags?: React.ReactNode;
  summary?: React.ReactNode;
  facts?: { label: string; value: React.ReactNode }[];
  actions?: React.ReactNode;
  sections: CrmDetailSection[];
  recordKey?: string;
  onClose: () => void;
  onRetry: () => void;
  children?: React.ReactNode;
}) {
  const contentRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [recordKey, open]);
  return (
    <Modal
      rootClassName="platform-ui"
      className={styles.modal}
      open={open}
      onCancel={onClose}
      width="min(1440px, calc(100vw - 48px))"
      centered
      footer={null}
      closable={{ 'aria-label': '关闭' }}
      keyboard
      focusable={{ trap: true, focusTriggerAfterClose: true }}
      transitionName=""
      maskTransitionName=""
      modalRender={(node) => <div className={styles.overlay}>{node}</div>}
      title={
        <div className={styles.header}>
          <div className={styles.heading}>
            <div className={styles.title}>
              <Typography.Title
                level={2}
                ellipsis={{
                  rows: 2,
                  tooltip: typeof title === 'string' ? title : undefined,
                }}
              >
                {title}
              </Typography.Title>
            </div>
            <div className={styles.actions}>
              <DetailActions>{actions}</DetailActions>
            </div>
          </div>
          {tags && <div className={styles.tags}>{tags}</div>}
          {summary && <div className={styles.summary}>{summary}</div>}
          {facts && (
            <dl className={styles.facts}>
              {facts.slice(0, 4).map((fact) => (
                <div key={fact.label}>
                  <dt>{fact.label}</dt>
                  <dd
                    title={
                      typeof fact.value === 'string' ? fact.value : undefined
                    }
                  >
                    {fact.value === '' ? '-' : (fact.value ?? '-')}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      }
      styles={{ body: { padding: 0, overflow: 'hidden', minHeight: 0 } }}
    >
      {loading ? (
        <div className={styles.feedback}>
          <Skeleton active paragraph={{ rows: 10 }} />
        </div>
      ) : error ? (
        <div className={styles.feedback}>
          <Alert
            type={
              errorStatus === 403 || errorStatus === 404 ? 'warning' : 'error'
            }
            showIcon
            title={
              errorStatus === 403
                ? '暂无权限查看此记录'
                : errorStatus === 404
                  ? '记录不存在或已删除'
                  : error
            }
            action={
              errorStatus === 403 ? undefined : errorStatus === 404 ? (
                <Button onClick={onClose}>关闭</Button>
              ) : (
                <Button onClick={onRetry}>重试</Button>
              )
            }
          />
        </div>
      ) : children != null ? (
        <div className={styles.content}>{children}</div>
      ) : (
        <main ref={contentRef} className={styles.content}>
          {sections.map((section) => (
            <div key={section.key} className={styles.section}>
              {section.hasOwnShell ||
              React.Children.toArray(section.children).some(
                (node) =>
                  React.isValidElement(node) && node.type === PageSection,
              ) ? (
                section.children
              ) : (
                <PageSection title={section.label}>
                  {section.children}
                </PageSection>
              )}
            </div>
          ))}
        </main>
      )}
    </Modal>
  );
}
